/**
 * Attaches documents to the tax profile, through a modal with two tabs: one picks files that
 * already sit in the client's team, the other sends files from the person's own computer.
 *
 * Expects the client id and the client's Teams team id, and the callbacks the page refreshes its
 * two reads with and says on screen that an add is in flight with.
 *
 * Renders the two tabs; on the pick tab, the two folder trees with a box and a document type select
 * on each file's row; on the upload tab, the file chooser and one type select for the whole batch;
 * and under both, the confirm button, which carries how many of the ticked files have settled while
 * the pick tab's requests are going out.
 *
 * The two tabs ask for a document type differently because the api does. An upload has to name one
 * (uploadTaxDocumentsBodySchema in api/routers/validators/taxProfileValidator.ts requires it). A
 * file picked from the team does not: a row written with no type is read with the type worked out
 * from the file itself, so a ticked file's select may be left alone. Neither tab asks for a year:
 * the tax year is the Year column of the document's row.
 */

import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import type { FileItem } from '@types';
import {
  isOcrDocumentType,
  OcrDocumentType,
  TAX_DOCUMENT_UPLOAD_EXTENSIONS,
  taxDocumentFileCanBeAdded,
  taxDocumentMaxFileBytes,
} from '@shared/constants/taxProfile';
import type {
  AddTaxDocumentFromTeamDriveRequest,
  AddTaxDocumentsResponse,
} from '@shared/types/taxProfile';
// eslint-disable-next-line max-len
import { SelectFileOrFolderAccordion } from '@/components/FileBrowser/modals/select-file/SelectFileOrFolderAccordion';
// eslint-disable-next-line max-len
import { FileItemExtended } from '@/components/UsersAccordionList/EditPermissionsModalSteps/interfaces';
// eslint-disable-next-line max-len
import { hasSubItems } from '@/components/UsersAccordionList/EditPermissionsModalSteps/utils';
import { MultiFileUpload } from '@/components/MultiFileUpload/MultiFileUpload';
import { GenericModal } from '@/components/Modal';
import { Spinner } from '@/components/Spinner';
import {
  alertErrorMessage,
  alertMessageSuccessNode,
  alertWarningMessageNode,
} from '@/utils/alerts';
import useFileData from '@/hooks/useFileData';
import useTaxProfile from '@/hooks/useTaxProfile';
import type {
  AddTaxDocumentsOutcome,
  TeamDriveFileProgress,
} from '@/hooks/useTaxProfile';
import {
  DOCUMENTS_SECTION_ID,
  scrollToId,
} from '@/pages/TaxProfile/components/DocumentsTable';
import { documentTypeOptions } from '@/pages/TaxProfile/components/ChooseDocumentTypeModal';
import {
  ADD_DOCUMENT_FAIL_SENTENCES,
  ADD_DOCUMENT_SKIP_SENTENCES,
  MAX_FILE_MB,
  MAX_SPREADSHEET_MB,
  cannotAddFilesSentence,
  tooLargeToReadLabel,
  tooLargeAdvice,
} from '@/pages/TaxProfile/wording';

export type AddDocumentChoice = 'pick' | 'upload';

export interface AddDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: number;
  /** The id of the client's Teams team the pick tab browses; null when the client has no team. */
  teamId: string | null;
  /**
   * The legal entity selected in the page's dropdown, which every file is added to. Null when the
   * client has no legal entity yet, and the api then creates the first one.
   */
  entityId: number | null;
  /**
   * Called after the api accepted the files, so the page can fetch both reads again. Also called
   * after a batch was refused part way, because files sent before the refusal may already be added.
   */
  onAdded: () => void | Promise<void>;
  /**
   * Called with how many files the modal is about to send and which tab is sending them, so the
   * page's left column can say an add is in flight. onAdded takes that line off once the api has
   * accepted the files, and onAddFailed takes it off once the api has refused them.
   */
  onAddStarted?: (count: number, choice: AddDocumentChoice) => void;
  onAddFailed?: () => void;
}

const documentCount = (count: number) =>
  count === 1 ? '1 document' : `${count} documents`;

/**
 * What the toast says once every file has settled: how many were added, why the api left files out
 * (`skipped`), and which files could not be added because something went wrong (`failed`), which
 * the person is asked to try again.
 */
export const addedSummary = (
  result: AddTaxDocumentsResponse &
    Partial<Pick<AddTaxDocumentsOutcome, 'failed'>>
): string => {
  const failed = result.failed ?? [];
  const added = `Added ${documentCount(
    result.created.length
  )} to the tax profile.`;
  const leftOut =
    result.skipped.length === 0
      ? ''
      : ` Left out ${result.skipped
          .map(
            (skip) =>
              `${skip.name}: ${
                ADD_DOCUMENT_SKIP_SENTENCES[skip.reason] ??
                'it could not be added'
              }`
          )
          .join('; ')}.`;
  const couldNotAdd =
    failed.length === 0
      ? ''
      : ` Couldn’t add ${failed
          .map(
            (one) => `${one.name} (${ADD_DOCUMENT_FAIL_SENTENCES[one.reason]})`
          )
          .join(', ')}. Try ${failed.length === 1 ? 'it' : 'them'} again.`;
  return `${added}${leftOut}${couldNotAdd}`;
};

const UPLOAD_CHOICE_MESSAGE =
  `Choose a PDF, an image, a spreadsheet or a Word document, up to ${MAX_FILE_MB} MB each ` +
  `(${MAX_SPREADSHEET_MB} MB for a .xlsx or .csv spreadsheet).`;

/**
 * Said under the confirm button while files are being sent. Closing the modal only hides it: the
 * requests are sent by useTaxProfile, which keeps sending, and opening the modal again shows how
 * far they have got. Closing the tab is what would lose the files not yet sent.
 */
const KEEP_THE_TAB_OPEN_SENTENCE =
  'You can close this window and the files keep being added. Keep this tab open until they are done.';

/**
 * The two folders of the client's team the pick tab browses, in the order it draws them.
 *
 * Both are found by name among the children of the team node. The OneDrive sync sets that node's
 * children to the children of the folder named 'External' (api/src/oneDrive/oneDrive.ts), so both
 * folders sit directly under it, and FileItem (shared/types/files.ts) carries no path, so the name
 * is the only thing that can find them.
 */
const PICK_FOLDER_NAMES = ['Taxes', 'Company Documents'];

/** Why a file put in the client's team a moment ago may not be in the two trees yet. */
const FILES_ARE_AS_FRESH_AS_THE_LAST_SYNC =
  'These are the files as of the last time we read the client’s team, so a file put there in the ' +
  'last few minutes may not be listed yet.';

/** What the pick tab says in place of a folder the client's team does not have. */
const folderNotFoundSentence = (name: string) =>
  `There is no folder called ${name} in this client’s team.`;

/** What the pick tab says in place of a folder that is there and holds no files. */
const folderIsEmptySentence = (name: string) => `${name} holds no files.`;

/** The option a ticked file starts on, meaning nobody has said what the document is. */
const PICK_AUTOMATICALLY_OPTION = 'Pick automatically';

/** The line above the two trees, which says a file's document type select may be left alone. */
const TYPE_IS_OPTIONAL_SENTENCE =
  'A type is optional. Leave a file on Pick automatically and we work out what the document is ' +
  'from the file itself; choose a type and we read the file as that type.';

/** The two columns a folder's list of files has, named above the rows of every folder. */
const FILE_COLUMN_LABEL = 'File';
const DOCUMENT_TYPE_COLUMN_LABEL = 'Document type';

/** The box above both trees, which ticks every file both folders hold. */
const SELECT_EVERY_FILE = 'Select every file';

/** The box in a folder's own header, which ticks every file that folder holds. */
const selectEveryFileIn = (folderName: string) =>
  `Select every file in ${folderName}`;

/** How many files a folder holds, said beside its name in its header. */
const fileCountSentence = (count: number) =>
  count === 1 ? '1 file' : `${count} files`;

/**
 * What the confirm button says while the requests are going out, on either tab: how many of the
 * files have settled, out of how many are being sent.
 *
 * One request is sent per file, and each one copies or uploads that file's bytes through the api
 * process, so a batch takes many seconds; without this count the button says nothing between the
 * click and the toast.
 */
const addProgressSentence = (settled: number, ticked: number) =>
  `${settled} of ${ticked} ${ticked === 1 ? 'file' : 'files'} done`;

/**
 * What a ticked file's own row says while the pick tab's requests are going out, one word per state
 * the file can be in: no request sent for it yet, its request in flight, a row written for it, and
 * no row written for it.
 */
const FILE_IS_WAITING = 'Waiting';
const FILE_IS_BEING_SENT = 'Adding';
const FILE_WAS_ADDED = 'Added';
const FILE_IS_ALREADY_ATTACHED = 'Already attached';
const FILE_WAS_NOT_ADDED = 'Not added';

/**
 * True for a file the OneDrive sync stored a size for that is above the limit
 * taxDocumentMaxFileBytes gives its name. A file with no size stored is not: the api asks Graph for
 * the size before it copies the file, and refuses it there.
 */
const fileIsTooLarge = (file: FileItem): boolean =>
  file.size != null && file.size > taxDocumentMaxFileBytes(file.name);

/** Every file a folder holds, and every file the folders under it hold. */
const filesUnder = (item: FileItem): FileItem[] =>
  item.type === 'file' ? [item] : (item.files ?? []).flatMap(filesUnder);

/**
 * The box in a header, which ticks every file under that header and unticks them again.
 *
 * Expects `files`, every file the header covers, including the ones the profile cannot store;
 * `selectedFileIds`, the ids ticked now; and onTick and onUntick, which the modal ticks and unticks
 * with.
 *
 * Renders one checkbox, drawn full when every file that can be stored is ticked, part-filled when
 * some are, and empty otherwise.
 *
 * onTick is handed the files the profile cannot store as well, so the modal can say why it left
 * them out, but they are left out of the count the box is drawn from, and so are the files too
 * large to read: a folder holding one such file would otherwise never draw a full box however many
 * files were ticked.
 */
function SelectFilesCheckbox({
  id,
  ariaLabel,
  files,
  selectedFileIds,
  onTick,
  onUntick,
}: {
  id?: string;
  ariaLabel?: string;
  files: FileItem[];
  selectedFileIds: ReadonlySet<string>;
  onTick: (files: FileItem[]) => void;
  onUntick: (files: FileItem[]) => void;
}) {
  const canBeAdded = files.filter(
    (file) => taxDocumentFileCanBeAdded(file.name) && !fileIsTooLarge(file)
  );
  const tickedCount = canBeAdded.filter((file) =>
    selectedFileIds.has(file.id)
  ).length;
  const allAreTicked =
    canBeAdded.length > 0 && tickedCount === canBeAdded.length;
  const someAreTicked = tickedCount > 0 && !allAreTicked;

  // A box is drawn part-filled only by setting the indeterminate property on the element itself.
  // There is no attribute for it, so it cannot be written as JSX.
  const box = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (box.current) box.current.indeterminate = someAreTicked;
  }, [someAreTicked]);

  return (
    <input
      type='checkbox'
      ref={box}
      id={id}
      aria-label={ariaLabel}
      className='checkbox checkbox-xs checkbox-accent shrink-0'
      disabled={canBeAdded.length === 0}
      checked={allAreTicked}
      onChange={() => (allAreTicked ? onUntick(files) : onTick(files))}
    />
  );
}

/**
 * The body of the toast the modal raises after the api accepted the files.
 *
 * Expects the sentence addedSummary built.
 *
 * Renders that sentence and a See documents button, which scrolls the page down to the Documents
 * table; the modal closes over that table, so the rows it just made are off screen.
 */
export function AddedToast({ message }: { message: string }) {
  return (
    <span>
      <span>{message}</span>{' '}
      <button
        type='button'
        className='btn btn-link btn-xs p-0 min-h-0 h-auto align-baseline normal-case'
        onClick={() => scrollToId(DOCUMENTS_SECTION_ID)}
      >
        See documents
      </button>
    </span>
  );
}

/**
 * The pick tab's two folder trees.
 *
 * Expects the client id and team id, the ids ticked now, and the callbacks that tick one file, tick
 * a list of files and untick a list of files, plus renderFileControl, which draws what sits at the
 * right end of a ticked file's row.
 *
 * Renders one header and one tree per folder of PICK_FOLDER_NAMES, with a box on every file, a box
 * in each folder's header that ticks that folder's files, and a box above both that ticks every
 * file both folders hold.
 *
 * It reads the client's files through useFileData rather than being handed them, and the modal
 * renders it only while the pick tab is showing, so opening the Tax Profile page costs no request
 * for them. The trees are SelectFileOrFolderAccordion, which the rest of the dashboard browses a
 * team with; the boxes on the files are two optional props that component takes.
 */
function TeamFolderTrees({
  clientId,
  teamId,
  selectedFileIds,
  onSelectedFileChange,
  onTickFiles,
  onUntickFiles,
  renderFileControl,
  fileCanBeSelected,
}: {
  clientId: number;
  teamId: string;
  selectedFileIds: ReadonlySet<string>;
  onSelectedFileChange: (file: FileItem) => void;
  onTickFiles: (files: FileItem[]) => void;
  onUntickFiles: (files: FileItem[]) => void;
  /** The document type select of a ticked file, drawn at the right end of that file's row. */
  renderFileControl: (file: FileItem) => ReactNode;
  /** False for a file whose box is drawn disabled: one too large to read. */
  fileCanBeSelected: (file: FileItem) => boolean;
}) {
  const { getFileData } = useFileData();
  const { data: rootFolder } = getFileData(clientId);
  const team = rootFolder?.files?.find((item: FileItem) => item.id === teamId);
  const folders = PICK_FOLDER_NAMES.map((name) => ({
    name,
    folder: team?.files?.find((item: FileItem) => item.name === name),
  }));
  const everyFile = folders.flatMap(({ folder }) =>
    folder ? filesUnder(folder) : []
  );

  return (
    <div className='mb-3'>
      <p className='text-xs opacity-70'>
        {FILES_ARE_AS_FRESH_AS_THE_LAST_SYNC}
      </p>
      {/* This line sits above the trees because the document type select it describes is on the
          row of each ticked file. */}
      <p className='text-xs opacity-70 mb-2'>{TYPE_IS_OPTIONAL_SENTENCE}</p>
      <div className='flex items-center gap-2 mb-1'>
        <SelectFilesCheckbox
          id='tax-document-select-every-file'
          files={everyFile}
          selectedFileIds={selectedFileIds}
          onTick={onTickFiles}
          onUntick={onUntickFiles}
        />
        <label
          className='text-sm font-semibold cursor-pointer'
          htmlFor='tax-document-select-every-file'
        >
          {SELECT_EVERY_FILE}
        </label>
        <span className='text-xs opacity-70'>
          {fileCountSentence(everyFile.length)}
        </span>
      </div>
      {folders.map(({ name, folder }, index) => (
        <div key={`tax-document-folder-${name}`}>
          {/* The line that separates Taxes from Company Documents. */}
          {index > 0 && <div className='divider my-2' />}
          <div className='flex items-center gap-2 mb-1'>
            <SelectFilesCheckbox
              ariaLabel={selectEveryFileIn(name)}
              files={folder ? filesUnder(folder) : []}
              selectedFileIds={selectedFileIds}
              onTick={onTickFiles}
              onUntick={onUntickFiles}
            />
            <h4 className='font-semibold text-sm'>{name}</h4>
            {folder && (
              <span className='text-xs opacity-70'>
                {fileCountSentence(filesUnder(folder).length)}
              </span>
            )}
          </div>
          {!folder && (
            <p className='text-warning text-sm'>
              {folderNotFoundSentence(name)}
            </p>
          )}
          {folder && (folder.files ?? []).length === 0 && (
            <p className='text-sm opacity-70'>{folderIsEmptySentence(name)}</p>
          )}
          {folder && (folder.files ?? []).length > 0 && (
            // No padding at the top, so the label row below sits flush with the top of the box and
            // no file row shows above it while the list scrolls.
            <div className='max-h-64 overflow-y-auto border border-base-300 rounded px-1 pb-1'>
              {/* One label row per folder: a single row above the first folder would sit above the
                  Company Documents header rather than above the rows it names. Its widths repeat
                  the widths of a file row below it — w-[95%] for the row itself, then w-4 for the
                  box, flex-1 for the icon and the name, w-48 for the type select, and the eye
                  button's own width. It is sticky so the labels stay while the list scrolls, and it
                  carries the look of a daisyUI `.table thead th`, which is what DocumentsTable.tsx
                  renders: small, bold, upper case, on base-200. */}
              <div
                className={
                  'sticky top-0 z-10 flex items-center gap-2 py-1 pr-1 w-[95%] ' +
                  'bg-base-200 border-b border-base-300 text-xs font-bold uppercase'
                }
              >
                <span className='w-4 shrink-0' />
                <span className='flex-1 min-w-0'>{FILE_COLUMN_LABEL}</span>
                <span className='w-48 shrink-0'>
                  {DOCUMENT_TYPE_COLUMN_LABEL}
                </span>
                {/* The width the eye button measures at, so that the label above the select
                    finishes where the select finishes. */}
                <span className='w-[30px] shrink-0' />
              </div>
              {folder.files?.map((child) => (
                <SelectFileOrFolderAccordion
                  clientId={clientId}
                  file={child}
                  level={0}
                  open={false}
                  fatherIsOpen
                  showAccordionIcon={hasSubItems(child as FileItemExtended)}
                  // SelectFileOrFolderAccordion draws no check button on a row that carries a box,
                  // so this is never called here. It is the callback the box uses, so the two
                  // controls cannot come to mean different things.
                  onSelectFile={onSelectedFileChange}
                  selectedFileIds={selectedFileIds}
                  onSelectedFileChange={onSelectedFileChange}
                  renderFileControl={renderFileControl}
                  fileCanBeSelected={fileCanBeSelected}
                  key={`tax-document-pick-${child.id}`}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function AddDocumentModal({
  isOpen,
  onClose,
  clientId,
  teamId,
  entityId,
  onAdded,
  onAddStarted,
  onAddFailed,
}: AddDocumentModalProps) {
  const { addFromTeamDrive, uploadDocuments } = useTaxProfile();
  const [choice, setChoice] = useState<AddDocumentChoice>('pick');
  const [docType, setDocType] = useState<OcrDocumentType | ''>('');
  /** The files ticked in the two trees, in the order they were ticked. */
  const [picked, setPicked] = useState<FileItem[]>([]);
  /** The type chosen for a ticked file, by that file's drive item id; absent means none. */
  const [typeForFile, setTypeForFile] = useState<
    Record<string, OcrDocumentType>
  >({});
  const [filesToSend, setFilesToSend] = useState<File[]>([]);
  // Each entry is one line of what the modal says about the files it held back.
  const [problem, setProblem] = useState<string[] | null>(null);
  // How to make the files held back for their size smaller, said below those lines in plain text
  // rather than in red, because it is advice, not another file that was held back.
  const [sizeAdvice, setSizeAdvice] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  /**
   * What each ticked file is doing while the pick tab's requests are going out, by that file's
   * drive item id. A ticked file with no entry here has had no request sent for it yet.
   */
  const [sendStateForFile, setSendStateForFile] = useState<
    Record<string, TeamDriveFileProgress>
  >({});
  /** How many of the files chosen on the upload tab have settled while they are being sent. */
  const [uploadSettledCount, setUploadSettledCount] = useState(0);

  const pickedIds = useMemo(
    () => new Set(picked.map((file) => file.id)),
    [picked]
  );

  /** How many of the ticked files have settled, which is the count the confirm button says. */
  const settledCount = picked.filter((file) => {
    const state = sendStateForFile[file.id]?.state;
    return state === 'added' || state === 'not_added';
  }).length;

  const reset = () => {
    setChoice('pick');
    setDocType('');
    setPicked([]);
    setTypeForFile({});
    setFilesToSend([]);
    setProblem(null);
    setSizeAdvice(null);
    setSending(false);
    setSendStateForFile({});
    setUploadSettledCount(0);
  };

  /**
   * Closes the modal. While files are being sent it only hides it: the state is kept, so opening it
   * again shows how far the files have got, and confirm below resets it once they have settled.
   * GenericModal's close icon hides the modal itself before it calls this, so this cannot refuse.
   */
  const close = () => {
    if (!sending) reset();
    onClose();
  };

  /**
   * How far one ticked file has got, drawn to the left of that file's document type select so the
   * select keeps the place the Document type column label sits over.
   *
   * Renders nothing at all before the confirm button is pressed, and nothing on a file outside the
   * pick tab. A file with no request sent for it yet says Waiting only while the batch is still
   * going: once the api has refused one file no further file is started, and the files that did
   * settle keep saying what became of them, so a person who presses the button again knows which
   * ones were added.
   */
  const renderSendState = (file: FileItem) => {
    if (choice !== 'pick') return null;
    const progress = sendStateForFile[file.id];
    if (!progress) {
      if (!sending) return null;
      return (
        <span className='badge badge-ghost badge-sm shrink-0'>
          {FILE_IS_WAITING}
        </span>
      );
    }
    if (progress.state === 'sending') {
      return (
        <span className='flex items-center gap-1 text-xs shrink-0'>
          <Spinner className='w-3 h-3 shrink-0' />
          {FILE_IS_BEING_SENT}
        </span>
      );
    }
    if (progress.state === 'added') {
      return (
        <span className='badge badge-success badge-sm shrink-0'>
          {FILE_WAS_ADDED}
        </span>
      );
    }
    return (
      <span className='badge badge-error badge-sm shrink-0'>
        {progress.reason === 'already_attached'
          ? FILE_IS_ALREADY_ATTACHED
          : FILE_WAS_NOT_ADDED}
      </span>
    );
  };

  /** The document type select of a ticked file, which sits at the right end of that file's row. */
  const renderDocumentTypeSelect = (file: FileItem) => (
    <select
      className='select select-bordered select-xs w-48'
      aria-label={`Document type for ${file.name}`}
      value={typeForFile[file.id] ?? ''}
      onChange={(event) => {
        const chosen = event.target.value;
        setTypeForFile((current) => {
          const next = { ...current };
          if (isOcrDocumentType(chosen)) next[file.id] = chosen;
          else delete next[file.id];
          return next;
        });
      }}
    >
      <option value=''>{PICK_AUTOMATICALLY_OPTION}</option>
      {documentTypeOptions(true)}
    </select>
  );

  /**
   * What the tree draws at the right end of a ticked file's row: how far that file has got while
   * the requests are going out, and the file's document type select. It renders nothing for a file
   * that is not ticked, so an unticked row is a box, an icon and a name.
   *
   * typeForFile keeps what a person chose under the file's id and nothing removes it, so unticking
   * a file and ticking it again brings back the type chosen for it. bodiesForPickedFiles reads only
   * the files in `picked`, so a type left behind by a file that ended up unticked is not sent.
   */
  const renderRowControls = (file: FileItem) => {
    if (fileIsTooLarge(file)) {
      return (
        <span className='badge badge-ghost badge-sm shrink-0 opacity-70'>
          {tooLargeToReadLabel(file.name)}
        </span>
      );
    }
    if (!pickedIds.has(file.id)) return null;
    return (
      <div className='flex items-center gap-2'>
        {renderSendState(file)}
        {renderDocumentTypeSelect(file)}
      </div>
    );
  };

  /** Ticks a file, or unticks one that is already ticked. */
  const handleSelectFile = (file: FileItem) => {
    if (fileIsTooLarge(file)) return;
    if (!taxDocumentFileCanBeAdded(file.name)) {
      setProblem([cannotAddFilesSentence([file.name])]);
      return;
    }
    setProblem(null);
    setSizeAdvice(null);
    setPicked((current) =>
      current.some((one) => one.id === file.id)
        ? current.filter((one) => one.id !== file.id)
        : [...current, file]
    );
  };

  /**
   * Ticks every one of these files, leaving out the ones the profile cannot store and saying so
   * when there were any, because the box would otherwise look as if it had missed them. A file too
   * large to read is left out without a sentence: its row already says so. The files already ticked
   * stay ticked, so the box in one folder's header does not untick the other folder's files.
   */
  const handleTickFiles = (files: FileItem[]) => {
    const storable = files.filter((file) =>
      taxDocumentFileCanBeAdded(file.name)
    );
    const canBeAdded = storable.filter((file) => !fileIsTooLarge(file));
    const notStorable = files.filter(
      (file) => !taxDocumentFileCanBeAdded(file.name)
    );
    setProblem(
      notStorable.length === 0
        ? null
        : [cannotAddFilesSentence(notStorable.map((file) => file.name))]
    );
    setPicked((current) => [
      ...current,
      ...canBeAdded.filter(
        (file) => !current.some((one) => one.id === file.id)
      ),
    ]);
  };

  /** Unticks every one of these files, leaving the rest of the ticked files alone. */
  const handleUntickFiles = (files: FileItem[]) => {
    const ids = new Set(files.map((file) => file.id));
    setProblem(null);
    setSizeAdvice(null);
    setPicked((current) => current.filter((one) => !ids.has(one.id)));
  };

  /**
   * Keeps the chosen files that can be sent, any number of them: uploadDocuments sends them one per
   * request, so how many are chosen is not what the api holds at once. A file over the limit
   * taxDocumentMaxFileBytes gives its name is held back here and named with the same label the pick
   * tab draws on such a file's row.
   */
  const handleSelectFiles = (files: File[]) => {
    // A file of a type the profile cannot store is named once, for its type: telling the person to
    // make it smaller would be advice for a file that can never be added.
    const tooBig = files.filter(
      (file) =>
        taxDocumentFileCanBeAdded(file.name) &&
        file.size > taxDocumentMaxFileBytes(file.name)
    );
    const wrongKind = files.filter(
      (file) => !taxDocumentFileCanBeAdded(file.name)
    );
    const accepted = files.filter(
      (file) =>
        file.size <= taxDocumentMaxFileBytes(file.name) &&
        taxDocumentFileCanBeAdded(file.name)
    );
    // Both reasons are said when the files chosen include some of each, so nobody fixes the type
    // of one file and then finds out about the size of another. One line per reason, and one per
    // file too large, then the advice on making those files smaller, shown as a list.
    const lines: string[] = [];
    if (wrongKind.length > 0) {
      lines.push(cannotAddFilesSentence(wrongKind.map((file) => file.name)));
    }
    tooBig.forEach((file) => {
      lines.push(`${file.name}: ${tooLargeToReadLabel(file.name)}.`);
    });
    setProblem(lines.length > 0 ? lines : null);
    setSizeAdvice(
      tooBig.length > 0 ? tooLargeAdvice(tooBig.map((file) => file.name)) : null
    );
    setFilesToSend(accepted);
  };

  // The `teamId !== null` test is a conjunct here rather than a check inside confirm, because that
  // is what narrows teamId to a string after the `if (!canConfirm) return` in confirm below.
  const canConfirm =
    !sending &&
    teamId !== null &&
    (choice === 'pick'
      ? picked.length > 0
      : docType !== '' && filesToSend.length > 0);

  /** One request body per ticked file, carrying a type only for the files that were given one. */
  const bodiesForPickedFiles = (
    team: string
  ): AddTaxDocumentFromTeamDriveRequest[] =>
    picked.map((file) => ({
      itemId: file.id,
      teamId: team,
      name: file.name,
      ...(typeForFile[file.id] ? { docType: typeForFile[file.id] } : {}),
      ...(entityId != null ? { entityId } : {}),
    }));

  const confirm = async () => {
    // canConfirm already says that there is something to send and, on the upload tab, that a
    // document type was chosen.
    if (!canConfirm) return;
    setSending(true);
    // Cleared here as well as in reset, so pressing the button again after a refusal starts every
    // row from Waiting rather than from what the refused batch left on it.
    setSendStateForFile({});
    setUploadSettledCount(0);
    onAddStarted?.(
      choice === 'pick' ? picked.length : filesToSend.length,
      choice
    );
    try {
      const result =
        choice === 'pick'
          ? await addFromTeamDrive(
              clientId,
              bodiesForPickedFiles(teamId),
              (progress) =>
                setSendStateForFile((current) => ({
                  ...current,
                  [progress.itemId]: progress,
                }))
            )
          : await uploadDocuments(
              clientId,
              filesToSend,
              docType as OcrDocumentType,
              entityId,
              () => setUploadSettledCount((count) => count + 1)
            );
      // A success only when every file was added. A file that failed, one that was left out (too
      // large, already attached, refused by OneDrive) or a batch that added nothing gets a warning,
      // so a toast saying "Added 0 documents" or "Left out" never looks like an all-clear.
      const everyFileAdded =
        result.failed.length === 0 &&
        result.skipped.length === 0 &&
        result.created.length > 0;
      const raise = everyFileAdded
        ? alertMessageSuccessNode
        : alertWarningMessageNode;
      raise(<AddedToast message={addedSummary(result)} />);
      await onAdded();
      // reset rather than close: close only hides the modal while `sending` is true, which it still
      // is in the render this function was made in.
      reset();
      onClose();
    } catch (error) {
      alertErrorMessage(
        (error as Error).message || 'The document could not be added'
      );
      onAddFailed?.();
      setSending(false);
      // sendOneRequestPerFile throws the batch's first refusal and drops the outcome of the files
      // already sent, which may have been added. Fetching the page's reads again shows those files,
      // so sending the batch again does not store them a second time as "name 1". onAdded is the
      // page's existing refresh; calling it from here leaves TaxProfile.tsx as it is.
      await onAdded();
    }
  };

  if (!isOpen) return null;

  return (
    // daisyUI's .modal-box carries `width: 91.6667%; max-width: 32rem`, and 32rem leaves a file
    // name about 260px once the box, the icon, the type select and the eye have taken their share
    // of a row. max-w-none drops that cap, so the width is the 91.6667% daisyUI already asks for
    // and the modal grows with the window. The class is passed to this one modal, so the other
    // modals GenericModal renders keep the 32rem.
    <GenericModal isOpen={isOpen} onClose={close} className='max-w-none'>
      <h3 className='font-bold text-lg mb-3'>
        Add a document to the tax profile
      </h3>

      {teamId === null && (
        <p className='text-warning text-sm mb-3'>
          This client has no Teams team yet, so there is nowhere to keep the
          document. Ask for the team to be created first.
        </p>
      )}

      <div className='tabs tabs-boxed mb-3' role='tablist'>
        <button
          type='button'
          role='tab'
          aria-selected={choice === 'pick'}
          className={`tab ${choice === 'pick' ? 'tab-active' : ''}`}
          disabled={teamId === null || sending}
          onClick={() => {
            setChoice('pick');
            setProblem(null);
            setSizeAdvice(null);
          }}
        >
          Pick from the client&apos;s files
        </button>
        <button
          type='button'
          role='tab'
          aria-selected={choice === 'upload'}
          className={`tab ${choice === 'upload' ? 'tab-active' : ''}`}
          disabled={teamId === null || sending}
          onClick={() => {
            setChoice('upload');
            setProblem(null);
            setSizeAdvice(null);
          }}
        >
          Upload from my computer
        </button>
      </div>

      {choice === 'pick' && teamId !== null && (
        <TeamFolderTrees
          clientId={clientId}
          teamId={teamId}
          selectedFileIds={pickedIds}
          onSelectedFileChange={handleSelectFile}
          onTickFiles={handleTickFiles}
          onUntickFiles={handleUntickFiles}
          renderFileControl={renderRowControls}
          fileCanBeSelected={(file) => !fileIsTooLarge(file)}
        />
      )}

      {choice === 'upload' && teamId !== null && (
        <div className='mb-3'>
          <MultiFileUpload
            multiple
            uploading={sending}
            uploadDone={false}
            acceptFiles={[...TAX_DOCUMENT_UPLOAD_EXTENSIONS]}
            onSelectFiles={handleSelectFiles}
            selectFileMessage={UPLOAD_CHOICE_MESSAGE}
          />
        </div>
      )}

      {choice === 'upload' && filesToSend.length > 0 && (
        <p className='text-sm mb-2'>
          Ready to send: {filesToSend.map((file) => file.name).join(', ')}
        </p>
      )}
      {problem &&
        (problem.length === 1 ? (
          <p className='text-error text-sm mb-2'>{problem[0]}</p>
        ) : (
          <ul className='text-error text-sm mb-2 list-disc pl-5 space-y-1'>
            {problem.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        ))}
      {sizeAdvice && <p className='text-sm mb-2 opacity-70'>{sizeAdvice}</p>}

      {/* Only the upload tab asks for one type for the whole batch. The route it sends to requires
          a type, and the pick tab asks for one file at a time, on that file's row. */}
      {choice === 'upload' && (
        <label className='form-control mb-4' htmlFor='tax-document-type'>
          <span className='label-text'>Document type</span>
          <select
            id='tax-document-type'
            className='select select-bordered select-sm w-full'
            value={docType}
            disabled={teamId === null}
            onChange={(event) =>
              setDocType(event.target.value as OcrDocumentType | '')
            }
          >
            <option value=''>Select a document type</option>
            {documentTypeOptions(true)}
          </select>
        </label>
      )}

      <div className='flex justify-end gap-2'>
        <button type='button' className='btn btn-ghost btn-sm' onClick={close}>
          {sending ? 'Close' : 'Cancel'}
        </button>
        {/* While the requests are going out the button carries a spinner and the count of the
            files that have settled, because one request is sent per file and ten of them take many
            seconds. */}
        <button
          type='button'
          className='btn btn-accent btn-sm'
          disabled={!canConfirm}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={confirm}
        >
          {sending ? (
            <>
              <Spinner className='w-4 h-4 shrink-0' />
              {choice === 'pick'
                ? addProgressSentence(settledCount, picked.length)
                : addProgressSentence(uploadSettledCount, filesToSend.length)}
            </>
          ) : (
            'Add to tax profile'
          )}
        </button>
      </div>
      {sending && (
        <p className='text-xs opacity-70 text-right mt-2'>
          {KEEP_THE_TAB_OPEN_SENTENCE}
        </p>
      )}
    </GenericModal>
  );
}
